import { format, type Locale } from "date-fns";
import { toDateLike } from "@racoon/lib";
import { Namespace, type TFn, type TranslationKey } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";

/** Only the fields the label reads, so a full Contract or a lighter embedded one both fit. */
export type ContractLabelSource = Partial<
  Pick<ConnectProto.Contract, "name" | "dealNumber" | "startAt" | "endAt">
>;

/** "14 дек. 2025" in the UI's locale (pass `useDateFnsLocale()`) — the design's long form for spans and period boxes. */
export const formatContractDay = (value: unknown, locale: Locale): string | null => {
  const date = toDateLike(value);
  return date ? format(date, "dd MMM yyyy", { locale }) : null;
};

/** "21.12.2025" — the design's short form, used in contract dropdown options. */
const formatContractDate = (value: unknown): string | null => {
  const date = toDateLike(value);
  return date ? format(date, "dd.MM.yyyy") : null;
};

/**
 * «<name> от 21.12.2025» — a contract cell (the pickup-locations cascade, the customers
 * table): its number when it has no name, the name alone without a start date; empty with
 * neither, for the caller's own placeholder.
 */
export const contractFromLabel = (contract: ContractLabelSource | undefined, t: TFn): string => {
  const name = contract?.name?.trim() || (contract?.dealNumber ? `№${contract.dealNumber}` : "");
  if (!name) return "";
  const date = formatContractDate(contract?.startAt);
  return date ? t(Namespace.PickupLocation, "table.contractFrom", { name, date }) : name;
};

/** The type's label from the Enum namespace; a missing or UNDEFINED type has none, so "—". */
export const contractTypeLabel = (type: ConnectProto.ContractType | undefined, t: TFn): string =>
  type
    ? t(Namespace.Enum, `CONTRACT_TYPE_${ConnectProto.ContractType[type]}` as TranslationKey)
    : "—";

/** "№12 от 14 дек. 2025 до 14 дек. 2026" — the participants table's contract cell. */
export const contractSpanLabel = (
  contract: ContractLabelSource,
  t: TFn,
  locale: Locale,
): string => {
  const from = formatContractDay(contract.startAt, locale);
  const to = formatContractDay(contract.endAt, locale);
  return from && to
    ? t(Namespace.PickupLocation, "participants.contractSpan", {
        number: contract.dealNumber,
        from,
        to,
      })
    : `№${contract.dealNumber}`;
};

/** "Договор №12 от 21.12.2025" — a contract as a dropdown option. */
export const contractOptionLabel = (contract: ContractLabelSource, t: TFn): string =>
  t(Namespace.PickupLocation, "participants.contractOption", {
    number: contract.dealNumber,
    date: formatContractDate(contract.startAt) ?? "—",
  });
